import {makeAutoObservable} from 'mobx';
import { IMarketData } from '../services/api/api.interfaces';
import apiService from '../services/api/api.service';
import {TickerData} from "../services/socket/socket.service";

export interface IMinMaxLeverage {
    minLeverage: number;
    maxLeverage: number;
}

export interface IMergedMarketData {
    symbol: string;
    baseAsset: string;
    quoteAsset: string;
    ticker: string;

    name: string;
    marketId: string;

    long?: IMarketData;
    short?: IMarketData;

    lastPrice?: string

    popular?: boolean
}

export class MarketsStore {
    defaultMarketId: string | null = null;
    currentMarketId: string | null = null;
    markets: Record<string, IMergedMarketData> = {};
    searchTerm: string = '';
    _selectingMarket: boolean = false;
    popularMarketsBaseAsset: string[] = ['trumpwins', 'harriswins'];

    constructor() {
        makeAutoObservable(this);
    }

    get currentMarket(): IMergedMarketData | null {
        return this.currentMarketId ? this.markets[this.currentMarketId] : null;
    }

    set selectingMarket(value: boolean) {
        this._selectingMarket = value;
    }
    get selectingMarket(): boolean {
        return this._selectingMarket;
    }

    get latestPrice() {
        return Number(marketsStore.currentMarket?.lastPrice) || 0;
    }

    get currentMarketMaxLeverage(): number {
        return Math.max(this.currentMarket?.long?.maxLeverage || 0, this.currentMarket?.short?.maxLeverage || 0)
    }

    get filteredMarkets() {

        const markets = Object.keys(this.markets)
            .map(key => this.markets[key])

        // set popular markets at the beginning
        const marketsSorted = []
        for (let popularMarketBaseAsset of this.popularMarketsBaseAsset) {
            const popularMarket = markets.find(m => m.baseAsset === popularMarketBaseAsset)
            if (popularMarket) {
                marketsSorted.push(popularMarket)
            }
        }
        for (let market of markets) {
            if (this.popularMarketsBaseAsset.includes(market.baseAsset)) {
                continue
            }
            marketsSorted.push(market)
        }

        return this.searchTerm
            ? marketsSorted.filter(
                (asset) =>
                    asset.baseAsset.toLowerCase().includes(this.searchTerm) ||
                    asset.quoteAsset.toLowerCase().includes(this.searchTerm)
            )
            : marketsSorted;
    }

    get marketsSize(): number {
        return Object.keys(this.markets).length
    }
    get allMarketTickers(): string[] {
        return Object.keys(this.markets).map(key => this.markets[key].ticker)
    }

    setSearchTerm = (term: string) => {
        this.searchTerm = term.trim().toLowerCase();
    };

    async fetchMarkets() {
        try {
            const markets = await apiService.marketsGet()
            this.setMarketsResponse(markets);
            if (this.filteredMarkets.length) {
                this.defaultMarketId = this.getMarketId(this.filteredMarkets[0]);
            }
            return markets
        } catch (err) {
            console.error('Failed to fetch markets:', err);
        }
    };

    private setMarketsResponse(markets: IMarketData[]) {
        this.markets = markets.reduce((res: Record<string, IMergedMarketData>, market) => {

            const marketId = this.getMarketId(market)

            if (!res[marketId]) {

                res[marketId] = {
                    name: `${market.baseAsset}/${market.quoteAsset}`.toUpperCase(),
                    marketId,
                    symbol: market.symbol,
                    baseAsset: market.baseAsset,
                    quoteAsset: market.quoteAsset,
                    ticker: market.ticker,
                    popular: this.popularMarketsBaseAsset.includes(market.baseAsset.toLowerCase())
                }

                if (market.lastTicker) {
                    const ask = market.lastTicker.a
                    const bid = market.lastTicker.b
                    res[marketId].lastPrice = ((Number(ask) + Number(bid)) / 2).toFixed(10);
                }
            }

            if (market.isShort) {
                res[marketId].short = market
            } else {
                res[marketId].long = market
            }

            return res
        }, {});
    }

    private getMarketId(market: IMarketData | IMergedMarketData) {
        return `${market.baseAsset.toUpperCase()}:${market.quoteAsset.toUpperCase()}`
    }

    selectMarket(marketId: string) {
        if (marketId && this.markets[marketId]) {
            this.currentMarketId = marketId;
        } else {
            this.currentMarketId = this.defaultMarketId;
        }
    }

    selectDefaultMarket() {
        if (this.defaultMarketId) {
            this.selectMarket(this.defaultMarketId);
        }
    }

    updateLatestPrice(lastTicker: TickerData) {
        if (!lastTicker) {
            return
        }
        const ask = lastTicker.a
        const bid = lastTicker.b
        const marketKey = Object.keys(this.markets).find(key => this.markets[key].ticker === lastTicker.s)
        if (marketKey) {
            this.markets[marketKey].lastPrice = ((Number(ask) + Number(bid)) / 2).toFixed(10);
        }
    }

    findMarketByName(marketName: string) {
        const marketIds = Object.keys(this.markets)
        for (const marketId of marketIds) {
            const market = this.markets[marketId]
            if (market.name === marketName) {
                return market
            }
        }

        return null;
    }
}

export const marketsStore = new MarketsStore();
